///
/// EarthBrowser
///
/// Created by Matt Giger on 10/25/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Seismological statistics for the cutout, all pure functions over the
/// sequence: magnitude of completeness and the Gutenberg-Richter b-value
/// (Aki maximum likelihood), Båth's law, a coarse Omori decay check, and a
/// least-squares plane through the hypocenters (the aftershock cloud *is*
/// the rupture surface) with its strike and dip.
///

import { type CutoutLayout, type SequenceEvent, sequenceTrend, toBlockXZ } from "./cutoutLayout"

const cMagBin = 0.1
const cMinEventsForB = 20
const cMinEventsForPlane = 8
const cDeg = 180 / Math.PI

export interface BValueEstimate {
	b: number
	/** Aki (1965) standard error b / sqrt(n) */
	stdErr: number
	/** Completeness magnitude used */
	mc: number
	/** Events at or above mc */
	n: number
}

/**
 * Magnitude of completeness by the maximum-curvature method: the most
 * populated 0.1 bin, plus the customary 0.2 correction. Undefined with
 * too few events to say
 */
export function completenessMagnitude(mags: number[]): number | undefined {
	if(mags.length < cMinEventsForB) return undefined
	const counts = new Map<number, number>()
	for(const m of mags) {
		const bin = Math.round(m / cMagBin)
		counts.set(bin, (counts.get(bin) ?? 0) + 1)
	}
	let bestBin = 0
	let best = -1
	for(const [bin, count] of counts) {
		if(count > best || (count === best && bin < bestBin)) {
			best = count
			bestBin = bin
		}
	}
	return Math.round((bestBin * cMagBin + 0.2) * 10) / 10
}

/**
 * Gutenberg-Richter b-value by Aki's maximum likelihood estimator over the
 * events at or above mc, corrected for the magnitude bin width
 */
export function bValue(mags: number[], mc?: number): BValueEstimate | undefined {
	const completeness = mc ?? completenessMagnitude(mags)
	if(completeness === undefined) return undefined
	const complete = mags.filter(m => m >= completeness - 1e-6)
	const n = complete.length
	if(n < cMinEventsForB) return undefined
	const mean = complete.reduce((s, m) => s + m, 0) / n
	const denom = mean - (completeness - cMagBin / 2)
	if(denom <= 0) return undefined
	const b = Math.LOG10E / denom
	return { b, stdErr: b / Math.sqrt(n), mc: completeness, n }
}

/** Båth's law: the largest aftershock is typically ~1.2 below the mainshock */
export function bathExpectedLargest(mainshockMag: number): number {
	return mainshockMag - 1.2
}

export interface OmoriCheck {
	/** Aftershocks in the first 24 h after the mainshock */
	firstDay: number
	/** Aftershocks in the most recent 24 h of the window */
	lastDay: number
	/** Days of aftershock observation available */
	spanDays: number
}

/** Coarse decay check: rate in the first day vs the latest day of data */
export function omoriCheck(events: SequenceEvent[], mainshockTimeMs: number, nowMs: number): OmoriCheck | undefined {
	const after = events.filter(e => e.inSequence && e.timeMs > mainshockTimeMs)
	const spanMs = nowMs - mainshockTimeMs
	if(after.length === 0 || spanMs < 2 * 86400000) return undefined
	const day = 86400000
	const firstDay = after.filter(e => e.timeMs - mainshockTimeMs <= day).length
	const lastDay = after.filter(e => nowMs - e.timeMs <= day).length
	return { firstDay, lastDay, spanDays: spanMs / day }
}

export interface PlaneFit {
	/** Centroid in true block km (x along layout strike, y = -depth, z across) */
	centroid: { x: number; y: number; z: number }
	/** Unit normal, y >= 0 */
	normal: { x: number; y: number; z: number }
	/** In-plane unit axes: along the plane's strike (horizontal) and down dip */
	strikeDir: { x: number; y: number; z: number }
	dipDir: { x: number; y: number; z: number }
	/** Half extents (2σ) of the cloud along strikeDir / dipDir, km */
	halfLength: number
	halfWidth: number
	/** Geographic strike (degrees clockwise from north, [0, 360)) and dip (degrees) */
	strikeDeg: number
	dipDeg: number
	/** Thickness / width ratio from the eigenvalues: 0 = perfectly planar, ~1 = a ball */
	flatness: number
	/**
	 * False when the catalog depths carry no information (most events pinned
	 * to a default depth, e.g. 10 km): the strike is still meaningful, the
	 * dip is an artifact and must not be shown
	 */
	dipResolved: boolean
	/** False when the epicenters form a round cloud: the strike is noise too */
	trendResolved: boolean
	n: number
}

/**
 * Least-squares plane through the sequence hypocenters in true km (depth
 * unstretched), via the covariance eigenvectors. Undefined for too few
 * events or a cloud with no dominant plane
 */
export function fitRupturePlane(events: SequenceEvent[], layout: CutoutLayout): PlaneFit | undefined {
	const pts: number[][] = []
	for(const e of events) {
		if(!e.inSequence) continue
		const xz = toBlockXZ(e.east - layout.centerEast, e.north - layout.centerNorth, layout.strike)
		pts.push([xz.x, -e.depthKm, xz.z])
	}
	const n = pts.length
	if(n < cMinEventsForPlane) return undefined

	// Depth information check: routine locations often fix depth at 10 km
	// (or another default); a cloud of such events is a horizontal sheet
	// whose "dip" says nothing about the fault
	const depths = pts.map(p => -p[1])
	const meanDepth = depths.reduce((s, d) => s + d, 0) / n
	const depthStd = Math.sqrt(depths.reduce((s, d) => s + (d - meanDepth) ** 2, 0) / n)
	const depthBins = new Map<number, number>()
	for(const d of depths) depthBins.set(Math.round(d), (depthBins.get(Math.round(d)) ?? 0) + 1)
	const modalShare = Math.max(...depthBins.values()) / n
	const dipResolved = depthStd >= 3 && modalShare < 0.5
	const trend = sequenceTrend(events)
	const trendResolved = !!trend && trend.sigmaMajor > 0 && trend.sigmaMinor / trend.sigmaMajor < 0.75

	const c = [0, 0, 0]
	for(const p of pts) for(let i = 0; i < 3; i++) c[i] += p[i] / n
	const cov = [[0, 0, 0], [0, 0, 0], [0, 0, 0]]
	for(const p of pts) {
		const d = [p[0] - c[0], p[1] - c[1], p[2] - c[2]]
		for(let i = 0; i < 3; i++) for(let j = 0; j < 3; j++) cov[i][j] += d[i] * d[j] / n
	}
	const { values, vectors } = jacobiEigen(cov)
	// Sort descending: vectors[0] longest axis, vectors[2] the normal
	const order = [0, 1, 2].sort((a, b) => values[b] - values[a])
	const major = vectors[order[0]]
	const normalRaw = vectors[order[2]]
	const sigma = order.map(i => Math.sqrt(Math.max(values[i], 0)))
	if(sigma[1] < 1e-3) return undefined
	const flatness = sigma[2] / sigma[1]
	// A cloud nearly as thick as it is wide has no plane to speak of
	if(flatness > 0.6) return undefined

	let normal = normalRaw
	if(normal[1] < 0) normal = normal.map(v => -v)
	const nx = normal[0], ny = normal[1], nz = normal[2]

	// Strike: horizontal direction in the plane. For a near-horizontal plane
	// the normal's horizontal part vanishes; use the major axis instead
	let strikeDir: number[]
	const horiz = Math.hypot(nx, nz)
	if(horiz > 1e-4) {
		strikeDir = normalize([-nz / horiz, 0, nx / horiz])
	} else {
		strikeDir = normalize([major[0], 0, major[2]])
	}
	// Down-dip: perpendicular to strike within the plane, pointing down
	let dipDir = normalize(cross(normal, strikeDir))
	if(dipDir[1] > 0) dipDir = dipDir.map(v => -v)
	// Right-hand rule (Aki & Richards): dip direction = strike + 90°; flip
	// the strike if the dip direction is on its left
	const azStrike = azimuthOf(strikeDir, layout)
	const azDip = azimuthOf(dipDir, layout)
	let strikeDeg = azStrike
	if(((azDip - azStrike + 360) % 360) > 180) {
		strikeDir = strikeDir.map(v => -v)
		strikeDeg = (azStrike + 180) % 360
	}
	const dipDeg = Math.acos(Math.min(1, Math.max(-1, ny))) * cDeg

	// Extents of the cloud along the in-plane axes (2σ)
	let varL = 0, varW = 0
	for(const p of pts) {
		const d = [p[0] - c[0], p[1] - c[1], p[2] - c[2]]
		const l = dot(d, strikeDir), w = dot(d, dipDir)
		varL += l * l / n
		varW += w * w / n
	}

	return {
		centroid: { x: c[0], y: c[1], z: c[2] },
		normal: { x: nx, y: ny, z: nz },
		strikeDir: { x: strikeDir[0], y: strikeDir[1], z: strikeDir[2] },
		dipDir: { x: dipDir[0], y: dipDir[1], z: dipDir[2] },
		halfLength: Math.max(2 * Math.sqrt(varL), 2),
		halfWidth: Math.max(2 * Math.sqrt(varW), 2),
		strikeDeg,
		dipDeg,
		flatness,
		dipResolved,
		trendResolved,
		n
	}
}

/** Difference between two strikes as undirected lines, degrees in [0, 90] */
export function strikeDifferenceDeg(a: number, b: number): number {
	const d = Math.abs(((a - b) % 180 + 180) % 180)
	return Math.min(d, 180 - d)
}

/** Geographic azimuth (degrees) of a horizontal block-frame direction */
export function azimuthOf(dir: number[], layout: CutoutLayout): number {
	const az = (layout.strike * cDeg + Math.atan2(dir[2], dir[0]) * cDeg) % 360
	return (az + 360) % 360
}

/**
 * Unit normal (y up) of a plane given its geographic strike and dip, in
 * the block frame (x along layout strike, z = strike + 90°)
 */
export function normalFromStrikeDip(strikeDeg: number, dipDeg: number, layout: CutoutLayout): { x: number; y: number; z: number } {
	const rel = strikeDeg / cDeg - layout.strike
	const dipAz = rel + Math.PI / 2
	const dip = dipDeg / cDeg
	// Up-normal tilts away from the dip direction
	return {
		x: -Math.cos(dipAz) * Math.sin(dip),
		y: Math.cos(dip),
		z: -Math.sin(dipAz) * Math.sin(dip)
	}
}

/** Angle in degrees between two plane normals, folded to [0, 90] */
export function planeAngleDeg(a: { x: number; y: number; z: number }, b: { x: number; y: number; z: number }): number {
	const d = Math.abs(a.x * b.x + a.y * b.y + a.z * b.z)
	return Math.acos(Math.min(1, d)) * cDeg
}

function dot(a: number[], b: number[]): number {
	return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
}

function cross(a: number[], b: number[]): number[] {
	return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]
}

function normalize(v: number[]): number[] {
	const len = Math.hypot(v[0], v[1], v[2]) || 1
	return v.map(x => x / len)
}

/** Eigen decomposition of a symmetric 3x3 by cyclic Jacobi rotations */
function jacobiEigen(m: number[][]): { values: number[]; vectors: number[][] } {
	const a = m.map(row => row.slice())
	const v = [[1, 0, 0], [0, 1, 0], [0, 0, 1]]
	for(let sweep = 0; sweep < 50; sweep++) {
		let off = 0
		for(let p = 0; p < 3; p++) for(let q = p + 1; q < 3; q++) off += a[p][q] * a[p][q]
		if(off < 1e-18) break
		for(let p = 0; p < 3; p++) {
			for(let q = p + 1; q < 3; q++) {
				if(Math.abs(a[p][q]) < 1e-15) continue
				const theta = (a[q][q] - a[p][p]) / (2 * a[p][q])
				const t = Math.sign(theta || 1) / (Math.abs(theta) + Math.sqrt(theta * theta + 1))
				const c = 1 / Math.sqrt(t * t + 1)
				const s = t * c
				for(let k = 0; k < 3; k++) {
					const akp = a[k][p], akq = a[k][q]
					a[k][p] = c * akp - s * akq
					a[k][q] = s * akp + c * akq
				}
				for(let k = 0; k < 3; k++) {
					const apk = a[p][k], aqk = a[q][k]
					a[p][k] = c * apk - s * aqk
					a[q][k] = s * apk + c * aqk
				}
				for(let k = 0; k < 3; k++) {
					const vkp = v[k][p], vkq = v[k][q]
					v[k][p] = c * vkp - s * vkq
					v[k][q] = s * vkp + c * vkq
				}
			}
		}
	}
	// Columns of v are the eigenvectors
	const vectors = [0, 1, 2].map(i => [v[0][i], v[1][i], v[2][i]])
	return { values: [a[0][0], a[1][1], a[2][2]], vectors }
}
